import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { FaEye } from "react-icons/fa";
import styles from "../List.module.css";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import { CustomButton, CustomDropdown } from "../../../../components";
import CustomMRTTable from "../../../../components/CustomMRTTable/CustomMRTTable";
import SecondaryAdmissionEditModal from "./SecondaryAdmissionEditModal";
import PopupModal from "../../../../components/PopupModal/PopupModal";
import ChecklistPopup from "./checklist/ChecklistPopup";
import BulkStatusChangeModal from "./Bulkstatuschangemodal";
import { useNavigate } from "react-router-dom";
import { fetchClassOptions } from "../../../../redux/Masterdata";
import moment from "moment";
import { PLAN, getPlanFeature } from "../../../../utils/access";

const genderOptions = [
  { label: "Male", value: "Male" },
  { label: "Female", value: "Female" },
  { label: "Others", value: "Others" },
  { label: "ஆண்", value: "ஆண்" },
  { label: "பெண்", value: "பெண்" },
  { label: "மற்றவை", value: "மற்றவை" },
];
const statusOptions = [
  { label: "All", value: "" },
  { label: "Preview", value: "Preview" },
  { label: "Submitted", value: "Submitted" },
  { label: "Proceed", value: "Proceed" },
  { label: "Seat Allocated", value: "Seat Allocated" },
  { label: "Interview Scheduled", value: "Interview Scheduled" },
  { label: "Approved", value: "Approved" },
  { label: "Rejected", value: "Rejected" },
  { label: "Withdrawn", value: "Withdrawn" },
  { label: "Invite Not Sent", value: "Invite Not Sent" },
  { label: "Admitted", value: "Admitted" },
  { label: "Dropped", value: "Dropped" },
];

const SecondaryAdmissionList = () => {
  const { translations } = useSelector((state) => state.language);

  const user = TokenService.getUser();
  const isCorePlan = getPlanFeature(user) === PLAN.CORE;
  const isCoreAiPlan = getPlanFeature(user) === PLAN.CORE_AI;
  const clientData = user?.client_data?.[0] || {};
  const client_id = clientData?.client_id || "";
  const school_id = clientData?.school_id || "";
  const academic_year_id = clientData?.academic_year_id || "";
  const academic_year = clientData?.academic_year || "";
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [data, setData] = useState([]);
  const [dates, setDates] = useState({
    completion_date: null,
    start_date: null,
    end_date: null,
    screening_start_date: null,
    screening_end_date: null,
    interview_start_date: null,
    interview_end_date: null,
    is_seatAllocate: 0,
  });
  const [loading, setLoading] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewRowData, setPreviewRowData] = useState(null);

  // ─── PopupModal state (confirm / success / failed) ───
  const [modalState, setModalState] = useState({
    open: false,
    type: "",
    message: "",
  });

  // ─── Pending action for PopupModal confirm ───
  const [pendingAction, setPendingAction] = useState(null);
  // e.g. { type: "seatAllocate" }
  // e.g. { type: "proceed", rows: [...], fromStatus: "Submitted" }

  // ─── BulkStatusChangeModal (approve / reject — needs reason) ───
  const [bulkModal, setBulkModal] = useState({
    show: false,
    type: "approve",
    rows: [],
  });

  // Pagination state
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);

  // Column filter state
  const [columnFilters, setColumnFilters] = useState([]);

  // Checkbox selection state
  const [selectedRows, setSelectedRows] = useState([]);
  const [showChecklistPopup, setShowChecklistPopup] = useState(false);
  const [checklistData, setChecklistData] = useState({
    id: "",
    is_secondary: 1,
    application_no: "",
  });

  // Ref to store latest filters for Enter key trigger
  const columnFiltersRef = useRef([]);

  // Keep ref in sync with state
  useEffect(() => {
    columnFiltersRef.current = columnFilters;
  }, [columnFilters]);

  // ─────────────────────────────────────────────
  //  Map MRT column accessorKey → API payload key
  // ─────────────────────────────────────────────
  const filterKeyMap = useMemo(
    () => ({
      application_no: "application_no",
      student_name: "name",
      preferred_class: "preferred_class",
      section: "section",
      medium: "preferred_first_language",
      gender: "gender",
      applied_date: "date",
      application_mode: "application_mode",
      status: "status",
    }),
    [],
  );

  const { classOptions } = useSelector((state) => state.masterData);

  const filteredClassOptions = classOptions?.filter(
    (item) => item.label !== "XI" && item.label !== "XII",
  );

  useEffect(() => {
    dispatch(
      fetchClassOptions({
        id: parseInt(school_id),
        academic_year: academic_year,
      }),
    );
  }, [dispatch, school_id, academic_year]);
  // const allApproved = useMemo(
  //   () =>
  //     data.length > 0 &&
  //     data.every(
  //       (row) => String(row.status).trim().toLowerCase() === "approved",
  //     ),
  //   [data],
  // );
  // Build API filter payload from given filters array
  const buildFilterPayload = useCallback(
    (filters) => {
      const payload = {
        application_no: "",
        name: "",
        preferred_class: "",
        section: "",
        preferred_first_language: "",
        date: "",
        gender: "",
        application_mode: "",
        status: "",
      };

      // (filters || []).forEach((filter) => {
      //   const apiKey = filterKeyMap[filter.id];
      //   if (apiKey && filter.value) {
      //     payload[apiKey] = filter.value;
      //   }
      // });
      (filters || []).forEach((f) => {
        if (f.value === "" || f.value === "All") return;

        // ✅ 1. CLASS (value → label)
        if (f.id === "preferred_class") {
          const matched = classOptions.find(
            (opt) => String(opt.value) === String(f.value),
          );

          payload.preferred_class = matched?.label || f.value;
          return;
        }

        // ✅ 2. GENDER (direct)
        if (f.id === "gender") {
          payload.gender = f.value;
          return;
        }

        // ✅ 3. STATUS (direct)
        if (f.id === "status") {
          payload.status = f.value;
          return;
        }

        // ✅ 4. REMAINING FIELDS (use mapping)
        const apiKey = filterKeyMap[f.id];
        if (apiKey) {
          payload[apiKey] = f.value;
        }
      });

      return payload;
    },
    [filterKeyMap, classOptions],
  );

  // ─────────────────────────────────────────────
  //  Action dropdown helpers (per-row)
  // ─────────────────────────────────────────────
  const getRowActionOptions = useCallback(
    (status) => {
      const normalized = (status || "").toLowerCase().replace(/\s+/g, "_");
      switch (normalized) {
        case "submitted":
          return [
            {
              label: translations?.labels?.proceed || "Proceed",
              value: "proceed",
            },
            {
              label: translations?.labels?.rejectedSmall || "Rejected",
              value: "rejected",
            },
          ];
        case "proceed":
          return [
            {
              label: translations?.labels?.rejectedSmall || "Rejected",
              value: "rejected",
            },
          ];
        case "seat_allocated":
        case "seat allocated":
          return [];
        case "interview_scheduled":
        case "interview scheduled":
          return [
            {
              label: translations?.labels?.approved || "Approved",
              value: "approved",
            },
            {
              label: translations?.labels?.rejectedSmall || "Rejected",
              value: "rejected",
            },
          ];
        case "approved":
          return [
            {
              label: translations?.labels?.rejectedSmall || "Rejected",
              value: "rejected",
            },
          ];
        case "rejected":
          return [
            {
              label: translations?.labels?.proceed || "Proceed",
              value: "proceed",
            },
          ];
        default:
          return [];
      }
    },
    [translations],
  );

  const isRowActionDisabled = useCallback((status) => {
    const normalized = (status || "").toLowerCase().replace(/\s+/g, "_");
    return (
      normalized === "seat_allocated" ||
      normalized === "seat allocated" ||
      ![
        "submitted",
        "proceed",
        "interview_scheduled",
        "interview scheduled",
        "approved",
        "rejected",
      ].includes(normalized)
    );
  }, []);

  // ─────────────────────────────────────────────
  //  Bulk status dropdown (derived from selection)
  // ─────────────────────────────────────────────
  const bulkStatusOptions = useMemo(() => {
    if (!selectedRows.length) return [];

    const normalizedStatuses = new Set(
      selectedRows.map((row) =>
        (row.status || "").toLowerCase().replace(/\s+/g, "_"),
      ),
    );

    if (normalizedStatuses.size !== 1) return [];

    const status = [...normalizedStatuses][0];

    const optionsMap = {
      submitted: [
        { label: translations?.labels?.proceed || "Proceed", value: "proceed" },
        {
          label: translations?.labels?.rejectedSmall || "Rejected",
          value: "rejected",
        },
      ],
      proceed: [
        {
          label: translations?.labels?.rejectedSmall || "Rejected",
          value: "rejected",
        },
      ],
      seat_allocated: [
        {
          label:
            translations?.labels?.alreadySeatAllocated ||
            "Already Seat Allocated",
          value: "seat_allocated",
        },
      ],
      interview_scheduled: [
        {
          label: translations?.labels?.approved || "Approved",
          value: "approved",
        },
        {
          label: translations?.labels?.rejectedSmall || "Rejected",
          value: "rejected",
        },
      ],
      approved: [
        {
          label: translations?.labels?.approved || "Already Approved",
          value: "approved",
        },
      ],
    };

    return optionsMap[status] || [];
  }, [selectedRows, translations]);

  const handleInterviewSchedule = () => {
    navigate("/user/admission-schedule", {
      state: {
        school_id: Number(school_id),
      },
    });
  };

  const isBulkDropdownDisabled = useMemo(() => {
    if (selectedRows.length === 0) return true;

    const statuses = [
      ...new Set(
        selectedRows.map((r) =>
          (r.status || "").toLowerCase().replace(/\s+/g, "_"),
        ),
      ),
    ];

    if (statuses.length !== 1) return true;

    const status = statuses[0];

    if (status === "seat_allocated" || status === "approved") return true;

    if (!["submitted", "proceed", "interview_scheduled"].includes(status))
      return true;

    return false;
  }, [selectedRows]);

  // ─────────────────────────────────────────────
  //  Button enable/disable derived states
  // ─────────────────────────────────────────────
  const isSeatAllocatedEnabled = useMemo(() => {
    const hasAnySeatAllocated = data.some(
      (row) =>
        (row.status || "").toLowerCase().replace(/\s+/g, "_") ===
        "seat_allocated",
    );

    if (hasAnySeatAllocated) {
      return false;
    }

    return data.some(
      (row) =>
        (row.status || "").toLowerCase().replace(/\s+/g, "_") === "proceed",
    );
  }, [data]);

  const seatButtonLabel = useMemo(() => {
    const hasAnySeatAllocated = data.some(
      (row) =>
        (row.status || "").toLowerCase().replace(/\s+/g, "_") ===
        "seat_allocated",
    );

    if (hasAnySeatAllocated) {
      return translations?.labels?.seatAllocated || "Seat Allocated";
    }

    return translations?.labels?.allocateSeat || "Allocate Seat";
  }, [data, translations]);

  const isChecklistEnabled = useMemo(() => {
    if (selectedRows.length !== 1) return false;

    const selectedRow = selectedRows[0];

    const status = (selectedRow.status || "")
      .toLowerCase()
      .replace(/\s+/g, "_");

    return status === "approved";
  }, [selectedRows]);
  useEffect(() => {
    fetchAdmissionList();
  }, [pagination.pageIndex, pagination.pageSize, columnFilters]);
  // ─────────────────────────────────────────────
  //  Fetch API
  // ─────────────────────────────────────────────
  const fetchAdmissionList = useCallback(
    async (filterOverrides) => {
      setLoading(true);
      try {
        const filters = buildFilterPayload(
          filterOverrides || columnFiltersRef.current,
        );
        const payload = {
          school_id: Number(school_id),
          academic_year_id: academic_year_id,
          ...filters,
          page: pagination.pageIndex + 1,
          limit: pagination.pageSize,
        };

        const response = await AxiosInstance.post(
          "/admission/listSecondryAdmission",
          payload,
        );

        if (response?.data?.status) {
          const list = response.data.data || [];
          setDates({
            completion_date: response?.data?.completion_date,
            start_date: response?.data?.start_date,
            end_date: response?.data?.end_date,
            screening_start_date: response?.data?.screening_start_date,
            screening_end_date: response?.data?.screening_end_date,
            interview_start_date: response?.data?.interview_start_date,
            interview_end_date: response?.data?.interview_end_date,
            is_seatAllocate: response?.data?.is_seatAllocate,
          });
          const formatted = list.map((item, index) => ({
            id: item.id || "",
            sno: pagination.pageIndex * pagination.pageSize + index + 1,
            application_no: item.application_no,
            student_name: item.student_name,
            preferred_class: item.preferred_class,
            section: item.assigned_section || "-",
            medium: item.preferred_first_language,
            gender: item.gender,
            applied_date: item.date?.split("T")[0] || "",
            application_mode: item.application_mode,
            status: item.status || "",
          }));
          setData(formatted);
          setRowCount(response.data.pagination?.total || formatted.length);
        } else {
          setData([]);
          setRowCount(0);
        }
      } catch (error) {
        console.error("Admission list error:", error);
        setData([]);
        setRowCount(0);
      } finally {
        setLoading(false);
        setSelectedRows([]);
      }
    },
    [
      school_id,
      academic_year_id,
      pagination.pageIndex,
      pagination.pageSize,
      buildFilterPayload,
    ],
  );

  // ─────────────────────────────────────────────
  //  Seat Allocate
  // ─────────────────────────────────────────────
  const handleSeatAllocate = () => {
    setPendingAction({ type: "seatAllocate" });
    setModalState({
      open: true,
      type: "Confirm",
      message:
        translations?.labels?.seatAllocateConfirm ||
        "You can seat allocate only once at a time. Do you want to continue?",
    });
  };

  const handleOfflineApplication = () => {
    navigate("/user/OfflineApplicationUpload", {
      state: {
        school_id: Number(school_id),
      },
    });
  };

  const allocateSeat = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {
        client_id: client_id,
        school_id: Number(school_id),
        user_id: user?.id,
      };

      const response = await AxiosInstance.post(
        "/admission/allocateSecondarySection",
        payload,
      );

      if (response?.data?.status) {
        setModalState({
          open: true,
          type: "success",
          message:
            translations?.labels?.seatAllocatedSuccess ||
            "Seat allocated successfully",
        });
        fetchAdmissionList();
      } else {
        const details = response?.data?.details;
        const suggestion = response?.data?.suggestion;
        const baseMessage =
          response?.data?.message ||
          translations?.labels?.somethingWentWrong ||
          "Something went wrong";

        const lines = [];

        lines.push(baseMessage);
        lines.push("");

        if (details && details.length > 0) {
          lines.push("Details:");
          details.forEach((detail, index) => {
            lines.push(`${index + 1}. ${detail}`);
          });
          lines.push("");
        }

        if (suggestion) {
          lines.push("Suggestion:");
          lines.push(suggestion);
        }

        setModalState({
          open: true,
          type: "Failed",
          message: lines.join("\n"),
          wide: true,
        });
      }
    } catch (error) {
      console.error("Seat allocation error:", error);
      setModalState({
        open: true,
        type: "Failed",
        message:
          translations?.labels?.somethingWentWrong || "Something went wrong",
      });
    } finally {
      setLoading(false);
    }
  }, [school_id, client_id, user?.id, translations, fetchAdmissionList]);

  // ─────────────────────────────────────────────
  //  Proceed Status Change API (no reason needed)
  // ─────────────────────────────────────────────
  const proceedStatusChange = useCallback(
    async (rows) => {
      setLoading(true);
      try {
        const payload = {
          ids: rows.map((row) => row.id),
          status: "Proceed",
          reason: "",
        };

        const response = await AxiosInstance.put(
          "/admission/secondry/bulk-status-change",
          payload,
        );

        if (response?.data?.status) {
          setModalState({
            open: true,
            type: "success",
            message:
              translations?.labels?.statusChangedSuccess ||
              "Status changed successfully!",
          });
          setSelectedRows([]);
          fetchAdmissionList();
        } else {
          setModalState({
            open: true,
            type: "Failed",
            message:
              response?.data?.message ||
              translations?.labels?.somethingWentWrong ||
              "Something went wrong",
          });
        }
      } catch (error) {
        console.error("Proceed status change error:", error);
        setModalState({
          open: true,
          type: "Failed",
          message:
            translations?.labels?.somethingWentWrong || "Something went wrong",
        });
      } finally {
        setLoading(false);
      }
    },
    [translations, fetchAdmissionList],
  );

  // ─────────────────────────────────────────────
  //  Dropped Status Change API (no reason needed)
  // ─────────────────────────────────────────────
  const droppedStatusChange = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {
        school_id: Number(school_id),
        academic_year: academic_year_id,
        is_secondary: 1,
      };

      const response = await AxiosInstance.post(
        "/admission/applicationDrop",
        payload,
      );

      if (response?.data?.status) {
        setModalState({
          open: true,
          type: "success",
          message:
            translations?.labels?.statusChangedSuccess ||
            "Status changed successfully!",
        });
        fetchAdmissionList();
      } else {
        setModalState({
          open: true,
          type: "Failed",
          message:
            response?.data?.message ||
            translations?.labels?.somethingWentWrong ||
            "Something went wrong",
        });
      }
    } catch (error) {
      console.error("Dropped status change error:", error);
      setModalState({
        open: true,
        type: "Failed",
        message:
          translations?.labels?.somethingWentWrong || "Something went wrong",
      });
    } finally {
      setLoading(false);
    }
  }, [school_id, academic_year_id, translations, fetchAdmissionList]);

  const handleMoveToDropped = () => {
    setPendingAction({ type: "dropped" });
    setModalState({
      open: true,
      type: "Confirm",
      message:
        translations?.labels?.moveToDroppedConfirm ||
        "Are you sure you want to move to dropped?",
    });
  };

  // ─────────────────────────────────────────────
  //  PopupModal close handler (confirm / cancel)
  // ─────────────────────────────────────────────
  const handleModalClose = useCallback(
    (confirmed) => {
      if (confirmed && modalState.type === "Confirm" && pendingAction) {
        if (pendingAction.type === "seatAllocate") {
          allocateSeat();
        } else if (pendingAction.type === "proceed") {
          proceedStatusChange(pendingAction.rows);
        } else if (pendingAction.type === "dropped") {
          droppedStatusChange();
        }
      }

      setModalState({ open: false, type: "", message: "", wide: false });
      setPendingAction(null);
    },
    [
      modalState.type,
      pendingAction,
      allocateSeat,
      proceedStatusChange,
      droppedStatusChange,
    ],
  );

  // Initial + pagination-triggered fetch
  useEffect(() => {
    if (school_id) {
      fetchAdmissionList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [school_id, pagination.pageIndex, pagination.pageSize]);

  // ─────────────────────────────────────────────
  //  Listen for Enter key inside MRT filter inputs
  // ─────────────────────────────────────────────
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== "Enter") return;

      const isFilterInput =
        e.target.tagName === "INPUT" &&
        (e.target.closest("thead") ||
          e.target.closest(".MuiTableHead-root") ||
          e.target.closest('[class*="MRT_TableHeadCell"]') ||
          e.target.closest('[class*="Header"]'));

      if (isFilterInput) {
        e.preventDefault();
        const latestFilters = columnFiltersRef.current;

        setPagination((prev) => {
          if (prev.pageIndex === 0) {
            fetchAdmissionList(latestFilters);
            return prev;
          }
          return { ...prev, pageIndex: 0 };
        });
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [fetchAdmissionList]);

  // ─────────────────────────────────────────────
  //  Checkbox handlers
  // ─────────────────────────────────────────────
  const isAllSelected = data.length > 0 && selectedRows.length === data.length;

  const handleSelectAll = useCallback(
    (e) => {
      if (e.target.checked) {
        setSelectedRows(data);
      } else {
        setSelectedRows([]);
      }
    },
    [data],
  );

  const handleSelectRow = useCallback((row) => {
    setSelectedRows((prev) => {
      const exists = prev.some(
        (item) => item.application_no === row.application_no,
      );

      if (exists) {
        return prev.filter(
          (item) => item.application_no !== row.application_no,
        );
      }

      return [...prev, row];
    });
  }, []);

  const handleCollectFeesClick = () => {
    navigate("/user/ListAdmissionCollectFees", {
      state: {
        admissionType: 0,
      },
    });
  };

  const handleChecklistClick = () => {
    if (selectedRows.length !== 1) return;

    const selectedRow = selectedRows[0];

    setChecklistData({
      id: selectedRow.id,
      is_secondary: 1,
      application_no: selectedRow.application_no || "",
    });

    setShowChecklistPopup(true);
  };

  // ─────────────────────────────────────────────
  //  Unified action handler (works for BOTH single row & bulk)
  //  rows: array of row objects to act on
  //  actionValue: "proceed" | "approved" | "rejected"
  //  fromStatus: current status string (for confirm message)
  // ─────────────────────────────────────────────
  const triggerAction = useCallback(
    (rows, actionValue, fromStatus) => {
      if (!actionValue || !rows.length) return;

      if (actionValue === "proceed") {
        // Show confirmation PopupModal — no reason needed
        setPendingAction({ type: "proceed", rows });
        setModalState({
          open: true,
          type: "Confirm",
          message: (
            translations?.labels?.confirmStatusChange ||
            "Are you sure? Change status from '{from}' to '{to}'?"
          )
            .replace("{from}", fromStatus || "Submitted")
            .replace("{to}", "Proceed"),
        });
      } else if (actionValue === "approved") {
        // Show BulkStatusChangeModal with reason
        setBulkModal({ show: true, type: "approve", rows });
      } else if (actionValue === "rejected") {
        // Show BulkStatusChangeModal with reason
        setBulkModal({ show: true, type: "reject", rows });
      }
    },
    [translations],
  );

  // ─────────────────────────────────────────────
  //  Row action handler (single row — column Action dropdown)
  // ─────────────────────────────────────────────
  const handleRowActionChange = useCallback(
    (row, actionValue) => {
      if (!actionValue) return;
      triggerAction([row], actionValue, row.status);
    },
    [triggerAction],
  );

  // ─────────────────────────────────────────────
  //  Bulk status handler (bulk — Bulk Change Status dropdown)
  // ─────────────────────────────────────────────
  const handleBulkStatusChange = useCallback(
    (value) => {
      if (!value) return;
      const actionValue = value?.value || value;

      if (!selectedRows.length) {
        setModalState({
          open: true,
          type: "Failed",
          message:
            translations?.labels?.noStudentsSelected || "No students selected",
        });
        return;
      }

      const fromStatus = selectedRows[0]?.status || "";
      triggerAction(selectedRows, actionValue, fromStatus);
    },
    [selectedRows, translations, triggerAction],
  );

  // ─────────────────────────────────────────────
  //  BulkStatusChangeModal callbacks
  // ─────────────────────────────────────────────
  const handleBulkSuccess = useCallback(() => {
    setModalState({
      open: true,
      type: "success",
      message:
        translations?.labels?.statusChangedSuccess ||
        "Status updated successfully!",
    });
    setSelectedRows([]);
    fetchAdmissionList();
  }, [translations, fetchAdmissionList]);

  const handleBulkError = useCallback((msg) => {
    setModalState({
      open: true,
      type: "Failed",
      message: msg,
    });
  }, []);

  // ─────────────────────────────────────────────
  //  Preview handler
  // ─────────────────────────────────────────────
  const handlePreview = useCallback((row) => {
    setPreviewRowData(row);
    setShowPreviewModal(true);
  }, []);

  // ─────────────────────────────────────────────
  //  Status color helper
  // ─────────────────────────────────────────────
  const getStatusStyle = (status) => {
    const normalized = (status || "").toLowerCase().replace(/\s+/g, "_");
    switch (normalized) {
      case "submitted":
        return { color: "var(--color-primary, #f57c00)", fontWeight: 600 };
      case "proceed":
        return { color: "#e53935", fontWeight: 600 };
      case "approved":
        return { color: "var(--color-success, #43a047)", fontWeight: 600 };
      case "rejected":
        return { color: "var(--color-error, #d32f2f)", fontWeight: 600 };
      case "seat_allocated":
      case "seat allocated":
        return { color: "#1565c0", fontWeight: 600 };
      case "interview_scheduled":
      case "interview scheduled":
        return { color: "#6a1b9a", fontWeight: 600 };
      default:
        return { color: "var(--text-secondary, #666)", fontWeight: 500 };
    }
  };

  // ─────────────────────────────────────────────
  //  Native <select> for Action column
  // ─────────────────────────────────────────────
  const ActionSelect = useCallback(
    ({ row }) => {
      const rowStatus = row.original.status;
      const options = getRowActionOptions(rowStatus);
      const disabled = isRowActionDisabled(rowStatus);

      return (
        <select
          disabled={disabled}
          defaultValue=""
          onChange={(e) => {
            const selectedValue = e.target.value;
            if (selectedValue) {
              handleRowActionChange(row.original, selectedValue);
            }
            e.target.value = ""; // reset dropdown after selection
          }}
          style={{
            padding: "6px 12px",
            borderRadius: "var(--border-radius-sm, 4px)",
            border: "1px solid var(--color-dark-grey, #ccc)",
            fontSize: "var(--font-size-sm, 13px)",
            backgroundColor: disabled ? "#f0f0f0" : "#fff",
            color: disabled ? "#aaa" : "#333",
            cursor: disabled ? "not-allowed" : "pointer",
            minWidth: 110,
            outline: "none",
          }}
        >
          <option value="" disabled>
            {translations?.labels?.action || "Action"}
          </option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    },
    [
      getRowActionOptions,
      isRowActionDisabled,
      handleRowActionChange,
      translations,
    ],
  );

  // ─────────────────────────────────────────────
  //  Columns definition
  // ─────────────────────────────────────────────
  const columns = useMemo(
    () => [
      {
        id: "select",
        header: (
          <input
            type="checkbox"
            checked={isAllSelected}
            onChange={handleSelectAll}
            className={styles.radioInput}
          />
        ),
        size: 40,
        Cell: ({ row }) => (
          <input
            type="checkbox"
            checked={selectedRows.some(
              (item) => item.application_no === row.original.application_no,
            )}
            onChange={() => handleSelectRow(row.original)}
            className={styles.radioInput}
          />
        ),
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: "actions_view",
        header: translations?.labels?.actions || "Actions",
        size: 70,
        Cell: ({ row }) => (
          <button
            className={styles.viewBtn}
            onClick={() => handlePreview(row.original)}
          >
            <FaEye />
          </button>
        ),
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "sno",
        header: translations?.labels?.serialNo || "S.No.",
        size: 70,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "application_no",
        header: translations?.labels?.applicationId || "Application ID",
        size: 150,
        enableSorting: true,
        enableColumnFilter: true,
      },
      {
        accessorKey: "student_name",
        header: translations?.labels?.name || "Name",
        size: 140,
        enableSorting: true,
        enableColumnFilter: true,
      },
      {
        accessorKey: "preferred_class",
        header: translations?.labels?.classLabel || "Class",
        size: 80,
        enableSorting: true,
        filterVariant: "select",
        filterSelectOptions: filteredClassOptions,
      },
      {
        accessorKey: "section",
        header: translations?.labels?.section || "Section",
        size: 90,
        enableSorting: true,
        enableColumnFilter: true,
      },
      {
        accessorKey: "medium",
        header: translations?.labels?.medium || "Medium",
        size: 100,
        enableSorting: true,
        enableColumnFilter: true,
      },
      {
        accessorKey: "gender",
        header: translations?.labels?.gender || "Gender",
        size: 90,
        enableSorting: true,
        filterVariant: "select",
        filterSelectOptions: genderOptions,
      },
      {
        accessorKey: "applied_date",
        header: translations?.labels?.appliedDate || "Applied Date",
        size: 130,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "application_mode",
        header: translations?.labels?.type || "Type",
        size: 90,
        enableSorting: true,
        enableColumnFilter: true,
      },
      {
        accessorKey: "status",
        header: translations?.labels?.status || "Status",
        size: 120,
        enableSorting: true,
        filterVariant: "select",
        filterSelectOptions: statusOptions,
        Cell: ({ cell }) => (
          <span style={getStatusStyle(cell.getValue())}>{cell.getValue()}</span>
        ),
      },
      // {
      //   id: "payment",
      //   header: translations?.labels?.payment || "Payment",
      //   size: 130,
      //   enableSorting: false,
      //   enableColumnFilter: false,
      //   Cell: ({ row }) =>
      //     (row.original.status || "").toLowerCase() === "admitted" ? (
      //       <CustomButton
      //         type="button"
      //         variant="contained"
      //         className={styles.payNowButton}
      //         fullWidth={false}
      //         onClick={() => navigate("/user/payfees")}
      //       >
      //         ₹ {translations?.labels?.payNow || "Pay Now"}
      //       </CustomButton>
      //     ) : null,
      // },
      {
        id: "action_dropdown",
        header: translations?.labels?.action || "Action",
        size: 150,
        Cell: ActionSelect,
        enableSorting: false,
        enableColumnFilter: false,
      },
    ],
    [
      translations,
      selectedRows,
      isAllSelected,
      ActionSelect,
      handleSelectAll,
      handleSelectRow,
      handlePreview,
      filteredClassOptions,
    ],
  );

  return (
    <div>
      {/* ─── Confirm / Success / Failed PopupModal ─── */}
      <PopupModal
        open={modalState.open}
        title={
          modalState.type === "success"
            ? translations?.labels?.success || "Success"
            : modalState.type === "Confirm"
              ? translations?.labels?.areYouSure || "Are you sure?"
              : modalState.type || "Failed"
        }
        message={modalState.message}
        onClose={handleModalClose}
        wide={modalState.wide || false}
      />

      {/* ─── Approve / Reject Modal (with reason) ─── */}
      <BulkStatusChangeModal
        show={bulkModal.show}
        onHide={() =>
          setBulkModal({ show: false, type: bulkModal.type, rows: [] })
        }
        type={bulkModal.type}
        selectedRows={bulkModal.rows}
        translations={translations?.labels}
        onSuccess={handleBulkSuccess}
        onError={handleBulkError}
        fetchAdmissionList={fetchAdmissionList}
        admissionType={"secondry"}
      />

      {/* PREVIEW MODAL */}
      <SecondaryAdmissionEditModal
        open={showPreviewModal}
        onClose={() => {
          setShowPreviewModal(false);
          setPreviewRowData(null);
        }}
        admissionId={previewRowData?.id}
        realAdmissionId={previewRowData?.application_no}
        data={previewRowData}
        fetchAdmissionList={fetchAdmissionList}
      />

      {/* ─── ACTION BUTTONS ─── */}
      <div className="row" style={{ alignItems: "center" }}>
        {isCoreAiPlan && (
          <div className="col-lg-3 col-md-4 col-sm-6 px-2 mb-2">
            <CustomButton
              type="button"
              variant="contained"
              onClick={handleOfflineApplication}
              className={styles.addButton}
              style={{ width: "100%" }}
            >
              {translations?.labels?.offlineApplication ||
                "+ Offline Application"}
            </CustomButton>
          </div>
        )}

        <div className="col-lg-3 col-md-4 col-sm-6 px-2 mb-2">
          <CustomButton
            type="button"
            variant="contained"
            className={styles.addButton}
            style={{ width: "100%" }}
            disabled={
              data.length === 0 ||
              dates.is_seatAllocate === 1 ||
              !data.some(
                (row) =>
                  (row.status || "").toLowerCase().replace(/\s+/g, "_") ===
                  "proceed",
              )
            }
            // disabled={!isSeatAllocatedEnabled}
            onClick={handleSeatAllocate}
          >
            {/* {seatButtonLabel} */}
            {dates.is_seatAllocate === 1 ? "Seat Allocated" : "Allocate Seat"}
          </CustomButton>
        </div>

        <div className="col-lg-3 col-md-4 col-sm-6 px-2 mb-2">
          <CustomButton
            type="button"
            variant="contained"
            className={styles.addButton}
            style={{ width: "100%" }}
            onClick={handleInterviewSchedule}
          >
            {translations?.labels?.interviewSchedule || "Interview Schedule"}
          </CustomButton>
        </div>

        {isCorePlan ? (
          <div className="col-lg-3 col-md-4 col-sm-6 px-2 mb-2">
            <CustomButton
              type="button"
              variant="contained"
              className={styles.addButton}
              style={{ width: "100%" }}
              onClick={handleChecklistClick}
              disabled={!isChecklistEnabled}
            >
              {translations?.labels?.checklist || "Checklist"}
            </CustomButton>
          </div>
        ) : (
          <div className="col-lg-3 col-md-4 col-sm-6 px-2 mb-2">
            <CustomButton
              type="button"
              variant="contained"
              className={styles.addButton}
              style={{ width: "100%" }}
              onClick={handleCollectFeesClick}
              // disabled={!allApproved}
            >
              {translations?.labels?.collectFees || "Collect Fees"}
            </CustomButton>
          </div>
        )}

        <div className="col-lg-3 col-md-4 col-sm-6 px-2">
          <CustomButton
            type="button"
            variant="contained"
            className={styles.addButton}
            style={{ width: "100%" }}
            onClick={handleMoveToDropped}
            disabled={
              !dates.completion_date ||
              moment(dates.completion_date).format("YYYY-MM-DD") >
                moment(new Date()).format("YYYY-MM-DD")
            }
          >
            {translations?.labels?.moveToDropped || "Move to Dropped"}
          </CustomButton>
        </div>

        <div className="col-lg-3 col-md-4 col-sm-6 px-2 mt-2">
          <CustomDropdown
            options={bulkStatusOptions}
            placeholder={
              translations?.labels?.bulkChangeStatus || "Bulk Change Status"
            }
            disabled={isBulkDropdownDisabled}
            onChange={handleBulkStatusChange}
          />
        </div>
      </div>

      {/* ─── TABLE ─── */}
      <CustomMRTTable
        columns={columns}
        data={data}
        loading={loading}
        pagination={pagination}
        rowCount={rowCount}
        onPaginationChange={setPagination}
        manualFiltering={true}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        enableColumnFilters={true}
        noRecordsMessage={
          translations?.labels?.noRecordsToDisplay || "No records to display"
        }
        enableSearch={false}
      />

      {showChecklistPopup && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <ChecklistPopup
              id={checklistData.id}
              is_secondary={checklistData.is_secondary}
              onCancel={() => {
                setShowChecklistPopup(false);
              }}
              checklistData={checklistData}
              fetchAdmissionList={fetchAdmissionList}
              user_id={user.id}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SecondaryAdmissionList;
